import React, { useState, useEffect } from 'react';
import { Users, CheckCircle2, Clock, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin-dashboard';
const WAVES = ['1', '2', '3', '4', '5'];

const Metric = ({ label, value, icon, testid }) => (
  <div className="adm-metric" data-testid={testid}>
    <div className="adm-metric-icon">{icon}</div>
    <div className="adm-metric-body">
      <span className="adm-metric-num">{value}</span>
      <span className="adm-metric-label">{label}</span>
    </div>
  </div>
);

const Bars = ({ rows, testid }) => {
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div className="adm-barchart" data-testid={testid}>
      {rows.map((r, i) => (
        <div className="adm-bar-row" key={r.label} data-testid={`${testid}-row-${i}`}>
          <span className="adm-bar-label" style={{ width: '45%', maxWidth: 210, minWidth: 0, flexShrink: 0, textAlign: 'left' }} title={r.label}>{r.label}</span>
          <div className="adm-bar-track"><div className="adm-bar-fill" style={{ width: `${(r.count / max) * 100}%`, display: r.count ? undefined : 'none' }} /></div>
          <span className="adm-bar-val">{r.count}</span>
        </div>
      ))}
    </div>
  );
};

const TesterTable = ({ testers }) => (
  <div className="adm-table-wrap">
    <table className="adm-table" data-testid="progress-tester-table">
      <thead><tr><th>Tester</th><th>Started</th><th>Reached</th><th>Answered</th><th>How far</th></tr></thead>
      <tbody>
        {testers.map(t => (
          <tr key={t.attempt_id} data-testid={`progress-row-${t.attempt_id}`}>
            <td><div className="adm-name-cell"><span className="adm-name">{t.name || '(no name)'}</span><span className="adm-email">{t.email}</span></div></td>
            <td className="adm-date">{t.started_at ? new Date(t.started_at).toLocaleDateString('en-NZ') : '-'}</td>
            <td>{t.completed ? <span className="adm-status completed">Finished</span>
              : t.last_qid ? <span style={{ fontSize: 13 }}>{t.last_qid} {t.last_topic}</span>
              : <span className="adm-status draft">Not started</span>}</td>
            <td>{t.answered}</td>
            <td style={{ minWidth: 140 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="adm-progress-track" style={{ flex: 1 }}>
                  <div className={`adm-progress-fill ${t.completed ? '' : 'partial'}`} style={{ width: `${t.percent}%` }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 34 }}>{t.percent}%</span>
              </div>
            </td>
          </tr>
        ))}
        {testers.length === 0 && <tr><td colSpan={5} className="adm-empty">No testers yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function AdminProgress({ token }) {
  const [wave, setWave] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    fetch(`${API}/progress${wave ? `?wave=${wave}` : ''}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); }).then(setData).catch(() => setData({ error: true }));
  }, [token, wave]);

  return (
    <div className="adm-section" data-testid="admin-progress-view">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Tester Progress</h2>
        <select className="adm-select" value={wave} onChange={e => setWave(e.target.value)} data-testid="progress-wave-filter">
          <option value="">All testers</option>
          {WAVES.map(w => <option key={w} value={w}>Wave {w}</option>)}
        </select>
      </div>
      {!data ? <div className="adm-loading">Loading progress...</div>
        : data.error ? <div className="adm-empty-msg">Could not load progress.</div> : (
        <>
          <div className="adm-metrics-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <Metric label="Testers" value={data.total} icon={<Users size={20} />} testid="progress-total" />
            <Metric label="Finished" value={data.completed} icon={<CheckCircle2 size={20} />} testid="progress-completed" />
            <Metric label="Stopped part way" value={data.in_progress - data.not_started} icon={<Clock size={20} />} testid="progress-partial" />
            <Metric label="Average progress" value={`${data.average_percent}%`} icon={<TrendingUp size={20} />} testid="progress-average" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <div className="adm-card">
              <h3>How many reached each section</h3>
              <Bars testid="progress-sections" rows={data.sections_reached.map(s => ({ label: s.section, count: s.count }))} />
            </div>
            <div className="adm-card">
              <h3>Where people most often stop</h3>
              <p className="adm-sub-text">Last question answered by testers who have not finished.{data.not_started ? ` ${data.not_started} opened it but answered nothing.` : ''}</p>
              {data.stop_points.length
                ? <Bars testid="progress-stops" rows={data.stop_points.map(s => ({ label: `${s.qid} ${s.topic}`, count: s.count }))} />
                : <div className="adm-empty-msg">No one has stopped part way.</div>}
            </div>
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--forest)', margin: '24px 0 12px' }}>Each tester</h3>
          <TesterTable testers={data.testers} />
        </>
      )}
    </div>
  );
}
